import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Copy, TicketPercent, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type {
  CouponCodeView,
  GenerateCouponRequest,
  GenerateCouponResponse,
  ResourcePlan,
} from '@claude-code-api/shared';

export default function AdminCouponsPage() {
  const { t } = useTranslation(['adminCoupons', 'common']);
  const [coupons, setCoupons] = useState<CouponCodeView[]>([]);
  const [plans, setPlans] = useState<ResourcePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deactivatingId, setDeactivatingId] = useState<string | null>(null);
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [planId, setPlanId] = useState('');
  const [durationDays, setDurationDays] = useState(30);
  const [maxRedemptions, setMaxRedemptions] = useState(1);
  const [expiresAt, setExpiresAt] = useState('');

  const openGenerator = () => {
    setGeneratedCode(null);
    setGeneratorOpen(true);
  };

  const fetchData = async () => {
    try {
      const [couponsRes, plansRes] = await Promise.all([
        fetch('/api/admin/coupons', { credentials: 'include' }),
        fetch('/api/admin/resource-plans', { credentials: 'include' }),
      ]);

      if (couponsRes.ok) {
        const payload = await couponsRes.json();
        setCoupons(payload.data || []);
      }
      if (plansRes.ok) {
        const payload = await plansRes.json();
        const activePlans = (payload.data || []).filter(
          (plan: ResourcePlan) => plan.isActive,
        );
        setPlans(activePlans);
        setPlanId((current) => current || activePlans[0]?.id || '');
      }
    } catch {
      toast.error(t('adminCoupons:errors.load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchData();
  }, []);

  const generateCoupon = async () => {
    if (!planId) {
      toast.error(t('adminCoupons:errors.planRequired'));
      return;
    }

    setSaving(true);
    try {
      const body: GenerateCouponRequest = {
        planId,
        durationDays,
        maxRedemptions,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      };
      const res = await fetch('/api/admin/coupons', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const payload = await res.json();
      if (!res.ok) {
        toast.error(payload.error || t('adminCoupons:errors.generate'));
        return;
      }

      const data = payload.data as GenerateCouponResponse;
      setGeneratedCode(data.code);
      toast.success(t('adminCoupons:messages.generated'));
      await fetchData();
    } catch {
      toast.error(t('adminCoupons:errors.generate'));
    } finally {
      setSaving(false);
    }
  };

  const deactivateCoupon = async (coupon: CouponCodeView) => {
    setDeactivatingId(coupon.id);
    try {
      const res = await fetch(`/api/admin/coupons/${coupon.id}/deactivate`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const payload = await res.json();
        toast.error(payload.error || t('adminCoupons:errors.deactivate'));
        return;
      }
      toast.success(t('adminCoupons:messages.deactivated'));
      await fetchData();
    } catch {
      toast.error(t('adminCoupons:errors.deactivate'));
    } finally {
      setDeactivatingId(null);
    }
  };

  const copyCouponCode = async (code: string | null | undefined) => {
    if (!code) {
      toast.error(t('adminCoupons:errors.copyUnavailable'));
      return;
    }
    try {
      await navigator.clipboard.writeText(code);
      toast.success(t('adminCoupons:messages.copied'));
    } catch {
      toast.error(t('adminCoupons:errors.copy'));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={t('adminCoupons:title')}
          description={t('adminCoupons:description')}
        />
        <Skeleton className="h-56 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('adminCoupons:title')}
        description={t('adminCoupons:description')}
        action={
          <Button onClick={openGenerator}>
            <TicketPercent className="mr-2 h-4 w-4" />
            {t('adminCoupons:generate.title')}
          </Button>
        }
      />

      <div className="overflow-hidden rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('adminCoupons:table.code')}</TableHead>
              <TableHead>{t('adminCoupons:table.plan')}</TableHead>
              <TableHead>{t('adminCoupons:table.duration')}</TableHead>
              <TableHead>{t('adminCoupons:table.redemptions')}</TableHead>
              <TableHead>{t('adminCoupons:table.expires')}</TableHead>
              <TableHead>{t('adminCoupons:table.status')}</TableHead>
              <TableHead>{t('adminCoupons:table.created')}</TableHead>
              <TableHead className="text-right">
                {t('adminCoupons:table.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {coupons.map((coupon) => (
              <TableRow key={coupon.id}>
                <TableCell className="font-mono">
                  {coupon.code || `****${coupon.codePreview}`}
                </TableCell>
                <TableCell>{coupon.plan?.name || coupon.planId}</TableCell>
                <TableCell>
                  {t('adminCoupons:table.days', {
                    count: coupon.durationDays,
                  })}
                </TableCell>
                <TableCell>
                  {coupon.timesRedeemed}/{coupon.maxRedemptions}
                </TableCell>
                <TableCell>
                  {coupon.expiresAt
                    ? new Date(coupon.expiresAt).toLocaleString()
                    : t('adminCoupons:table.never')}
                </TableCell>
                <TableCell>
                  <Badge variant={coupon.isActive ? 'default' : 'secondary'}>
                    {coupon.isActive
                      ? t('adminCoupons:status.active')
                      : t('adminCoupons:status.inactive')}
                  </Badge>
                </TableCell>
                <TableCell>
                  {new Date(coupon.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Button
                      size="icon"
                      variant="outline"
                      title={
                        coupon.code
                          ? t('adminCoupons:actions.copy')
                          : t('adminCoupons:actions.copyUnavailable')
                      }
                      aria-label={
                        coupon.code
                          ? t('adminCoupons:actions.copy')
                          : t('adminCoupons:actions.copyUnavailable')
                      }
                      onClick={() => {
                        void copyCouponCode(coupon.code);
                      }}
                      disabled={!coupon.code}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    {coupon.isActive && (
                      <Button
                        size="icon"
                        variant="destructive"
                        title={
                          deactivatingId === coupon.id
                            ? t('adminCoupons:actions.deactivating')
                            : t('adminCoupons:actions.deactivate')
                        }
                        aria-label={
                          deactivatingId === coupon.id
                            ? t('adminCoupons:actions.deactivating')
                            : t('adminCoupons:actions.deactivate')
                        }
                        onClick={() => {
                          void deactivateCoupon(coupon);
                        }}
                        disabled={deactivatingId === coupon.id}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {coupons.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="py-8 text-center text-sm text-muted-foreground"
                >
                  {t('adminCoupons:empty')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={generatorOpen} onOpenChange={setGeneratorOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TicketPercent className="h-5 w-5 text-primary" />
              {t('adminCoupons:generate.title')}
            </DialogTitle>
            <DialogDescription>
              {t('adminCoupons:description')}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="coupon-plan">{t('adminCoupons:form.plan')}</Label>
              <Select value={planId} onValueChange={setPlanId}>
                <SelectTrigger id="coupon-plan">
                  <SelectValue
                    placeholder={t('adminCoupons:form.planPlaceholder')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="coupon-duration-days">
                {t('adminCoupons:form.durationDays')}
              </Label>
              <Input
                id="coupon-duration-days"
                type="number"
                min={1}
                max={366}
                value={durationDays}
                onChange={(event) => setDurationDays(Number(event.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="coupon-max-redemptions">
                {t('adminCoupons:form.maxRedemptions')}
              </Label>
              <Input
                id="coupon-max-redemptions"
                type="number"
                min={1}
                max={10000}
                value={maxRedemptions}
                onChange={(event) => setMaxRedemptions(Number(event.target.value))}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="coupon-expires-at">
                {t('adminCoupons:form.expiresAt')}
              </Label>
              <Input
                id="coupon-expires-at"
                type="datetime-local"
                value={expiresAt}
                onChange={(event) => setExpiresAt(event.target.value)}
              />
            </div>
          </div>

          {generatedCode && (
            <div className="rounded-md border border-success/25 bg-success/10 p-3 text-sm text-foreground">
              <p className="font-medium">
                {t('adminCoupons:generate.generatedCode')}
              </p>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                <code className="flex-1 rounded border border-border bg-background px-3 py-2 font-mono text-base text-foreground">
                  {generatedCode}
                </code>
                <Button
                  size="icon"
                  variant="outline"
                  title={t('adminCoupons:actions.copy')}
                  aria-label={t('adminCoupons:actions.copy')}
                  onClick={() => {
                    void copyCouponCode(generatedCode);
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {t('adminCoupons:generate.oneTimeNotice')}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={() => {
                void generateCoupon();
              }}
              disabled={saving || !planId}
            >
              {saving
                ? t('adminCoupons:actions.generating')
                : t('adminCoupons:actions.generate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
